"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import RainforestPaymentLink from "@/components/rainforest/rainforest-payment-link";
import { Button } from "@/components/ui";
import { formatCurrency } from "@/lib/format-data";
import { binLookupFromStripeCard } from "@/lib/payments/card-funding";
import { submitActivePaymentSurface } from "@/lib/payments/payment-surface-registry";
import { PaymentProcessor } from "@/types/auth";
import { PaymentMethodType } from "@/types/enums/payment";
import { BinLookupResult } from "@/types/rainforest";
import StripePaymentSurface, { getStripe } from "./stripe-payment-surface";
interface PatientPaymentSurfaceProps {
  processor?: PaymentProcessor;
  /** Rainforest. */
  sessionKey?: string | null;
  payinConfigId?: string | null;
  sandbox?: boolean;
  allowedMethods?: string;
  /** Stripe. */
  publishableKey?: string | null;
  stripeAccount?: string | null;
  clientSecret?: string | null;
  /** Tenant policy; Stripe enforces domain registration itself. */
  allowApplePay?: boolean;
  /**
   * Offer Link. Defaults on *here* and nowhere else: this component is the
   * card form on a page a patient was sent to, so the person Link would enrol
   * is the person whose card it is. The staff surfaces leave
   * `StripePaymentSurface`'s own default, which is off.
   */
  allowLink?: boolean;

  /**
   * Always called with a reference: Rainforest's payment link reports the
   * payin id and Stripe the confirmed intent id. (The staff-facing
   * `CardEntrySurface` differs - Rainforest's terminal reports nothing there.)
   */
  onSuccess: (reference: string) => void;
  onFailure: (message: string) => void;
  onSessionConsumed?: () => void;
  /**
   * Card type before submission, for the surcharge line. Rainforest reports it
   * from a BIN lookup as the patient types; Stripe from a ConfirmationToken's
   * card preview once the number is complete. Same shape either way, so the
   * pages that read it do not branch on processor.
   */
  onBinLookup?: (result: BinLookupResult) => void;
  /** Which method the form is on, as the patient picks it. */
  onMethodTypeChange?: (methodType: PaymentMethodType | null) => void;
  /** The method last reported, so a Stripe form rebuilt by a fee change reopens on it. */
  methodType?: PaymentMethodType | null;
  /**
   * What the pay button says while the intent's own amount is still loading.
   * Only reaches the screen on Stripe - Rainforest's hosted component carries
   * its own button. Once the amount is known the button reads "Pay $X" from
   * the intent instead; see `useIntentAmount`.
   */
  payLabel?: string;
  className?: string;
}

/**
 * The card form on a page a patient was sent to.
 *
 * Separate from `CardEntrySurface` because the patient-facing pages hold the
 * session as loose fields rather than a `payinConfig` object, and because they
 * have no staff standing over them: the failure modes are different, and the
 * Rainforest side takes an `allowedMethods` string the staff surfaces do not.
 *
 * Wallets are the reason this could not ship with the staff surfaces. Under
 * direct charges Apple Pay and Google Pay are registered per connected account
 * *and* per the domain in the address bar — which for the embed is the clinic's
 * own website. Stripe hides a wallet whose domain is not registered, so the
 * button is absent rather than broken; `allowApplePay` carries the one thing
 * Stripe cannot know, which is that the clinic chose not to accept it.
 */
/**
 * What the PaymentIntent will actually charge, read from Stripe.
 *
 * The button used to say whatever the page computed, or "Pay now" when the
 * page computed nothing. Both were wrong in ways a patient could not see:
 *
 * - Booking fees passed no label, so a surcharging clinic's page said "Deposit
 *   of $25.00", the button said "Pay now", and the card was charged $25.75.
 *   The surcharge was never shown before the charge, which card-network rules
 *   require. Rainforest's hosted button always showed the payin amount, so
 *   this was Stripe-only.
 * - The payment link built its label from `totalOutstanding`, so after a
 *   patient chose a 50% plan the preview said "Due Today $1,030.00" while the
 *   button said "Pay $2,060.00" - for an intent that charged $1,030.00.
 *
 * The intent's `amount` is the one number that is always right: it is what
 * gets authorized, surcharge included. Keyed on the client secret, because a
 * debit/credit switch or a plan choice re-mints the intent rather than editing
 * it, so a new secret is exactly when the amount can change.
 */
function useIntentAmount(publishableKey, stripeAccount, clientSecret) {
  const $ = _c(5);
  const [amount, setAmount] = useState(null);
  let t0;
  let t1;
  if ($[0] !== clientSecret || $[1] !== publishableKey || $[2] !== stripeAccount) {
    t0 = () => {
      setAmount(null);
      if (!publishableKey || !stripeAccount || !clientSecret) {
        return;
      }
      let stale = false;
      getStripe(publishableKey, stripeAccount).then(stripe => stripe?.retrievePaymentIntent(clientSecret)).then(result => {
        const cents = result?.paymentIntent?.amount;
        if (!stale && typeof cents === "number") {
          setAmount(cents);
        }
      }).catch(_temp);
      return () => {
        stale = true;
      };
    };
    t1 = [publishableKey, stripeAccount, clientSecret];
    $[0] = clientSecret;
    $[1] = publishableKey;
    $[2] = stripeAccount;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  return amount;
}
function _temp() {}
export default function PatientPaymentSurface(t0) {
  const $ = _c(49);
  const {
    processor,
    sessionKey,
    payinConfigId,
    sandbox,
    allowedMethods,
    publishableKey,
    stripeAccount,
    clientSecret,
    allowApplePay: t1,
    allowLink: t2,
    onSuccess,
    onFailure,
    onSessionConsumed,
    onBinLookup,
    onMethodTypeChange,
    methodType,
    payLabel: t3,
    className
  } = t0;
  const allowApplePay = t1 === undefined ? true : t1;
  const allowLink = t2 === undefined ? true : t2;
  const payLabel = t3 === undefined ? "Pay now" : t3;
  const [submitting, setSubmitting] = useState(false);
  const [paid, setPaid] = useState(false);
  const intentAmount = useIntentAmount(processor === PaymentProcessor.STRIPE ? publishableKey : null, stripeAccount, clientSecret);
  if (processor === PaymentProcessor.STRIPE) {
    if (paid) {
      let t4;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <div className="w-full text-center"><div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center text-status-success-text"><FaCheckCircle size="64" /></div><h2 className="text-h2 text-foreground">Payment received</h2><p className="mt-2 text-body text-foreground-secondary">Thanks — there is nothing else for you to do.</p></div>;
        $[0] = t4;
      } else {
        t4 = $[0];
      }
      let t5;
      if ($[1] !== className) {
        t5 = <div className={className}>{t4}</div>;
        $[1] = className;
        $[2] = t5;
      } else {
        t5 = $[2];
      }
      return t5;
    }
    if (!publishableKey || !stripeAccount || !clientSecret) {
      let t4;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <span className="text-body text-status-error-text">This payment page is not finished being set up. Please contact the clinic.</span>;
        $[3] = t4;
      } else {
        t4 = $[3];
      }
      let t5;
      if ($[4] !== className) {
        t5 = <p className={className}>{t4}</p>;
        $[4] = className;
        $[5] = t5;
      } else {
        t5 = $[5];
      }
      return t5;
    }
    let t4;
    if ($[6] !== onSuccess) {
      t4 = reference => {
        setSubmitting(false);
        setPaid(true);
        onSuccess(reference);
      };
      $[6] = onSuccess;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    let t5;
    if ($[8] !== onFailure) {
      t5 = message => {
        setSubmitting(false);
        onFailure(message);
      };
      $[8] = onFailure;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[10] !== onSessionConsumed) {
      t6 = onSessionConsumed ? () => {
        setSubmitting(false);
        setPaid(true);
        onSessionConsumed();
      } : undefined;
      $[10] = onSessionConsumed;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    let t7;
    if ($[12] !== onBinLookup) {
      t7 = onBinLookup ? card => onBinLookup(binLookupFromStripeCard(card)) : undefined;
      $[12] = onBinLookup;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    let t8;
    if ($[14] !== allowApplePay || $[15] !== allowLink || $[16] !== clientSecret || $[17] !== methodType || $[18] !== onMethodTypeChange || $[19] !== publishableKey || $[20] !== stripeAccount || $[21] !== t4 || $[22] !== t5 || $[23] !== t6 || $[24] !== t7) {
      t8 = <StripePaymentSurface publishableKey={publishableKey} stripeAccount={stripeAccount} clientSecret={clientSecret} onSuccess={t4} onFailure={t5} allowApplePay={allowApplePay} allowLink={allowLink} audience="patient" onSessionConsumed={t6} onCardFunding={t7} onMethodTypeChange={onMethodTypeChange} methodType={methodType} />;
      $[14] = allowApplePay;
      $[15] = allowLink;
      $[16] = clientSecret;
      $[17] = methodType;
      $[18] = onMethodTypeChange;
      $[19] = publishableKey;
      $[20] = stripeAccount;
      $[21] = t4;
      $[22] = t5;
      $[23] = t6;
      $[24] = t7;
      $[25] = t8;
    } else {
      t8 = $[25];
    }
    let t9;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = () => {
        setSubmitting(true);
        if (!submitActivePaymentSurface()) {
          setSubmitting(false);
        }
      };
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    let t10;
    if ($[27] !== intentAmount || $[28] !== payLabel || $[29] !== submitting) {
      t10 = submitting ? "Processing\u2026" : intentAmount !== null ? `Pay ${formatCurrency(intentAmount)}` : payLabel;
      $[27] = intentAmount;
      $[28] = payLabel;
      $[29] = submitting;
      $[30] = t10;
    } else {
      t10 = $[30];
    }
    let t11;
    if ($[31] !== submitting || $[32] !== t10) {
      t11 = <Button variant="primary" className="mt-4 w-full" disabled={submitting} onClick={t9}>{t10}</Button>;
      $[31] = submitting;
      $[32] = t10;
      $[33] = t11;
    } else {
      t11 = $[33];
    }
    let t12;
    if ($[34] !== className || $[35] !== t11 || $[36] !== t8) {
      t12 = <div className={className}>{t8}{t11}</div>;
      $[34] = className;
      $[35] = t11;
      $[36] = t8;
      $[37] = t12;
    } else {
      t12 = $[37];
    }
    return t12;
  }
  const t4 = sessionKey ?? "";
  const t5 = payinConfigId ?? "";
  const t6 = sandbox ?? false;
  let t7;
  if ($[38] !== allowedMethods || $[39] !== className || $[40] !== onBinLookup || $[41] !== onFailure || $[42] !== onMethodTypeChange || $[43] !== onSessionConsumed || $[44] !== onSuccess || $[45] !== t4 || $[46] !== t5 || $[47] !== t6) {
    t7 = <RainforestPaymentLink onSuccess={onSuccess} onFailure={onFailure} onSessionConsumed={onSessionConsumed} onBinLookup={onBinLookup} onMethodTypeChange={onMethodTypeChange} sessionKey={t4} payinConfigId={t5} sandbox={t6} allowedMethods={allowedMethods} className={className} />;
    $[38] = allowedMethods;
    $[39] = className;
    $[40] = onBinLookup;
    $[41] = onFailure;
    $[42] = onMethodTypeChange;
    $[43] = onSessionConsumed;
    $[44] = onSuccess;
    $[45] = t4;
    $[46] = t5;
    $[47] = t6;
    $[48] = t7;
  } else {
    t7 = $[48];
  }
  return t7;
}